// Use environment variable for API base URL with fallback to production URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://kwcdp.co.za/backend/api';

// Helper function to ensure consistent media URLs
export const getMediaUrl = (path: string) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  
  // Remove any leading slashes from the path
  const cleanPath = path.replace(/^\/+/, '');
  
  // Base URL for media files
  const baseUrl = 'https://kwcdp.co.za';
  
  // If the path already includes /media/, use it as is
  if (path.includes('/media/')) {
    return `${baseUrl}/${cleanPath}`;
  }
  
  // For gallery images
  return `${baseUrl}/media/gallery/${cleanPath}`;
};

// Helper function to log API errors
function logApiError(operation: string, error: any) {
  console.error(`API Error (${operation}):`, {
    message: error.message,
    response: error.response,
    status: error.status,
    stack: error.stack
  });
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  errors?: string[];
}

async function handleResponse<T>(response: Response): Promise<T> {
  // First, get the response text to see what we're dealing with
  const responseText = await response.text();
  let data;
  
  try {
    // Try to parse the response as JSON
    data = JSON.parse(responseText);
  } catch (error) {
    // If it's not JSON, log the actual response for debugging
    console.error('Failed to parse JSON response. Response was:', responseText);
    console.error('Response headers:', Object.fromEntries(response.headers.entries()));
    
    // If the response is HTML, it's likely a server error page
    if (responseText.trim().startsWith('<!DOCTYPE') || 
        responseText.trim().startsWith('<html>')) {
      console.error('Received HTML response instead of JSON. This usually means:');
      console.error('1. The PHP server is not running or not accessible');
      console.error('2. The URL is incorrect');
      console.error('3. There was a server-side error');
      
      throw new Error('Server returned an HTML error page. Please check if the PHP backend is running.');
    }
    
    throw new Error('Invalid JSON response from server');
  }
  
  // Handle error responses
  if (!response.ok || (data && data.success === false)) {
    const errorMessage = data?.error || 
                        data?.errors?.join('\n') || 
                        `HTTP Error ${response.status}: ${response.statusText}`;
    
    console.error('API Error:', {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      error: errorMessage,
      responseData: data
    });
    
    throw new Error(errorMessage);
  }
  
  // Return the data property for successful responses
  if (data && typeof data === 'object' && 'data' in data) {
    return data.data as T;
  }
  
  // If we get here, the response format is unexpected
  console.error('Unexpected response format. Full response:', data);
  throw new Error('Unexpected response format from server');
}

export const api = {
  // Articles
  async getArticles() {
    const response = await fetch(`${API_BASE_URL}/articles.php`);
    const data = await handleResponse<any[]>(response);
    return data || [];
  },

  async createArticle(articleData: any) {
    const response = await fetch(`${API_BASE_URL}/articles.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(articleData),
      credentials: 'include'
    });
    const data = await handleResponse<any>(response);
    return data;
  },

  async deleteArticle(id: number) {
    const response = await fetch(`${API_BASE_URL}/articles.php?id=${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include'
    });
    const data = await handleResponse<{ success: boolean }>(response);
    return data.success;
  },

  // Galleries
  async getGalleries() {
    const response = await fetch(`${API_BASE_URL}/galleries.php`);
    const data = await handleResponse<any[]>(response);
    return data || [];
  },

  async createGallery(galleryData: any) {
    const response = await fetch(`${API_BASE_URL}/galleries.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(galleryData),
      credentials: 'include'
    });
    const data = await handleResponse<any>(response);
    return data;
  },

  async deleteGallery(id: number) {
    const response = await fetch(`${API_BASE_URL}/galleries.php?id=${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include'
    });
    const data = await handleResponse<{ success: boolean }>(response);
    return data.success;
  },

  // File Upload
  async uploadFiles(files: File[]): Promise<{ url: string }[]> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    const response = await fetch(`${API_BASE_URL}/upload.php`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    const data = await handleResponse<{ urls: string[] }>(response);
    return data.urls.map(url => ({ url }));
  },
};
